import {Component, EventEmitter, forwardRef, Input, OnInit, Output, TemplateRef} from '@angular/core';
import {ODataAdaptor} from '../../../adaptors/odata-adaptor';
import {DataManager, Query, Predicate} from '@syncfusion/ej2-data';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

const SEARCH_DELAY = 50;
const ID_FIELD = 'Id';
const SEARCH_FIELD = 'ChampRecherche';
const LIBELLE_FIELD = SEARCH_FIELD;
const SORTED_BY_FIELD = SEARCH_FIELD;
const CODE_COULEUR = 'CodeCouleur';
const MAX_RESULTS = 20;

enum Couleurs {
  blanc,
  rouge,
  orange,
  vert
}

export interface IdStringAutoCompleteSelectionModel {
  id: number;
  caption: string;
}

@Component({
  selector: 'app-idstring-autocomplete',
  templateUrl: './idstring-autocomplete.component.html',
  styleUrls: ['./idstring-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IdStringAutoCompleteComponent),
      multi: true,
    },
  ],
})
export class IdStringAutoCompleteComponent implements OnInit, ControlValueAccessor {
  searchTerm: string;
  listvisible = false;
  searchList: any[] = [];
  inputIsFocus = false;
  rowselected = 0;

  @Input() multiselect: boolean;
  @Input() selectedTemplate: TemplateRef<any>;
  @Input() itemTemplate: TemplateRef<any>;
  @Input() idField = ID_FIELD;
  @Input() libelleField = LIBELLE_FIELD;
  @Input() searchField = SEARCH_FIELD;
  @Input() codeCouleur = CODE_COULEUR;
  @Input() maxResults = MAX_RESULTS;
  @Input() sortedBy: string[] = [SORTED_BY_FIELD];
  @Input() isDisabled = false;
  @Input() undeletableIds: string[] = [];

  @Output() selected = new EventEmitter<any>();

  @Input() set url(value: string) {
    this._url = value;
    this.setupDatasource();
  }

  private subscription: Subscription;

  private cache: any[] = [];

  private searchDelayer = new Subject<string>();

  _url: string;

  private datasource: DataManager;

  _selectedId: string ;

  _selectedIdList: string[] = [];
  onChangeFn = null;

  set selectedId(value: string ) {
    if (value != this._selectedId) {
      this._selectedId = value;
      this.addMissingSelectedElementsIntoCache();
      this.loadElementIfNotInCache(value);
      if (this.onChangeFn) {
        this.onChangeFn(value);
      }
      this.emitSelection();
    }
  }

  set selectedIdList(value: string[]) {
    if (value != this._selectedIdList) {
      if (value == undefined) {
        value = [];
      }
      this._selectedIdList = value;
      this.addMissingSelectedElementsIntoCache();
      this.loadElementsIfNotInCache(value);
      if (this.onChangeFn) {
        this.onChangeFn(value);
      }
      this.emitSelection();
    }
  }

  private getItemId(item: object): string {

    if (item == null) {
      return null;
    }

    return String(item[this.idField]);
  }

  private getItemLibelle(item): string {
    if (item != null) {
      return item[this.libelleField];
    }
  }

  private addMissingSelectedElementsIntoCache() {
    if (this.multiselect) {
      this.selectedIdList.forEach(id => {
        if (!this.idExistsInCache(id)) {
          const elt = this.searchList.find(it => this.getItemId(it) == id);
          if (elt != null) {
            this.cache.push(elt);
          }
        }
      });
    } else {
      if (this.selectedId != null && !this.idExistsInCache(this.selectedId)) {
        const elt = this.searchList.find(it => this.getItemId(it) == this.selectedId);
        if (elt != null) {
          this.cache.push(elt);
        }
      }
    }
  }

  get selectedIdList() : string[] {
    return this._selectedIdList;
  }

  get selectedId(): string  {
    return this._selectedId;
  }

  private idExistsInCache(id: string): boolean {
    return this.cache.find(item => this.getItemId(item) === id) != null;
  }

  private idExistsInCacheOrInResults(id: string): boolean {
    return this.idExistsInCache(id) || this.searchList.find(it => this.getItemId(it) == id) != null;
  }

  private loadElementById(id: string) {
    if (id != null) {
      this.datasource.executeQuery(new Query().where(this.idField, 'equal', id)).then(result => {
        const resultats = result['result'];
        if (resultats && resultats.length > 0) {
          this.cache = [resultats[0]];
        }
      });
    }
  }

  get url(): string {
    return this._url;
  }

  constructor(private oDataAdaptor: ODataAdaptor) {
    this.subscription = this.searchDelayer.pipe(debounceTime(SEARCH_DELAY)).subscribe(searchTerm => {
      this.updateSearch(searchTerm);
    });
  }

  ngOnInit() {}

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  writeValue(obj: any): void {

    if (!this.multiselect) {
      this.selectedId = obj;
    } else {
      this.selectedIdList = obj;
    }
  }

  getItemFromCache(id: string) {
    return this.cache.find(it => this.getItemId(it) === id);
  }

  getLibelleById(id: string) {
    const elt = this.getItemFromCache(id);
    if (!elt) {
      return `${id}`;
    } else {
      return this.getItemLibelle(elt);
    }
  }

  private setupDatasource() {
    this.datasource = new DataManager({
      url: this.url,
      adaptor: this.oDataAdaptor,
    });
  }

  requestSearchUpdate($event: any) {
    this.searchTerm = $event;
    this.searchDelayer.next($event);
  }

  private updateSearch(searchTerm: string) {
    const query = new Query();
    query.where(this.searchField, 'contains', searchTerm, true);
    if (this.maxResults != null && this.maxResults > 0) {
      query.take(this.maxResults);
    }
    this.sortedBy.forEach(column => query.sortBy(column));
    this.datasource.executeQuery(query).then(resultat => {
      this.searchList = resultat['result'];
      this.showList();
    });
  }

  private showList() {
    this.listvisible = true;
  }

  selectElement($event: any) {
    if (!this.isAlreadySelected($event)) {
      if (!this.multiselect) {
        this.writeValue(this.getItemId($event));
        this.searchTerm = '';
        this.hideList();
      } else {
        this.addSelectedElementToList($event);
        this.searchTerm = '';
      }
    }
  }

  private addSelectedElementToList(elt) {

    const itemId = this.getItemId(elt);

    if (!this._selectedIdList.find(it => it === itemId)) {
      const listUpdated = [...this._selectedIdList, itemId];
      this.writeValue(listUpdated);
    }
  }

  hideList() {
    this.listvisible = false;
  }

  normalizeData(elt: any): IdStringAutoCompleteSelectionModel {
    if (elt != null) {
      return Object.assign({}, elt, {
        id: this.getItemId(elt),
        caption: this.getItemLibelle(elt),
      });
    } else {
      return null;
    }
  }

  private loadElementIfNotInCache(value: string) {
    if (!this.idExistsInCache(value)) {
      this.loadElementById(value);
    }
  }

  private loadElementsIfNotInCache(value: string[]) {
    if (value != null) {
      if (!value.every(id => this.idExistsInCacheOrInResults(id))) {
        this.loadElementsFromList(value);
      }
    }
  }

  private loadElementsFromList(value: string[]) {
    const predicates = value.reduce((previous: Predicate, id) => {
      if (!previous) {
        return new Predicate(this.idField, 'equal', id);
      } else {
        return previous.or(this.idField, 'equal', id);
      }
    }, null);

    this.datasource.executeQuery(new Query().where(predicates)).then(result => {
      this.searchList = result['result'];
      this.searchList.forEach(item => {
        this.addToCacheIfNotExist(item);
      });
    });
  }

  removeElement(id: string) {
    const idx = this._selectedIdList.indexOf(id);
    if (idx >= 0) {
      const modified = [...this._selectedIdList];
      modified.splice(idx, 1);
      this.writeValue(modified);
    }
  }

  changeRow(number: number) {
    this.rowselected += number;
    if (this.rowselected > this.searchList.length - 1) {
      this.rowselected = this.searchList.length - 1;
    }
    if (this.rowselected < 0) {
      this.rowselected = 0;
    }
  }

  selectElementByIndexWhenDropDownVisible(rowselected: number) {
    if (this.listvisible) {
      const elt = this.searchList[rowselected];
      this.selectElement(elt);
    }
  }

  selectRow(i: number) {
    this.rowselected = i;
  }

  isAlreadySelected(elt: any) {
    if (this.multiselect) {
      return this.selectedIdList.find(id => id === this.getItemId(elt));
    } else {
      return this.selectedId === this.getItemId(elt);
    }
  }

  dropDown() {
    if (this.listvisible) {
      this.hideList();
    } else {
      const searchTerm = this.searchTerm || '';
      this.requestSearchUpdate(searchTerm);
    }
  }

  private addToCacheIfNotExist(item: object) {
    if (!this.cache.find(element => this.getItemId(element) === this.getItemId(item))) {
      this.cache.push(item);
    }
  }

  clearSelectionIfInputEmpty() {
    if (this.searchTerm == null || this.searchTerm == '') {
      if (!this.multiselect) {
        this.writeValue(null);
      }
      else {
        const count: number = this.selectedIdList.length;

        if (count) {
          const lastId: string = this.selectedIdList[count-1];

          if (this.isElementSupprimable(lastId)) {
            const reducedList = [...this.selectedIdList];
            reducedList.pop();
            this.writeValue(reducedList);
          }
        }
      }
    }
  }

  private emitSelection() {
    if (!this.multiselect) {
      const item = this.normalizeData(this.getItemFromCache(this.selectedId));
      this.selected.emit(item);
    } else {
      const items = this.selectedIdList
        .map(it => this.normalizeData(this.getItemFromCache(it)))
        .filter(it => it != null);
      this.selected.emit(items);
    }
  }

  getCodeCouleur(elt: any): string {
    if (this.hasCouleur(elt)) {
      switch (elt[this.codeCouleur]) {
        case Couleurs.rouge: return 'rouge';
        case Couleurs.orange: return 'orange';
        case Couleurs.vert: return 'vert';
        default: return '';
      }
    } else {
      return '';
    }
  }

  hasCouleur(elt: any): boolean {
    return elt != null && elt[this.codeCouleur] && (elt[this.codeCouleur] > 0);
  }

  isElementSupprimable(i: string): boolean  {
    return !this.undeletableIds.includes(i);
  }
}
